import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  ResumeScreeningConfig,
  CreateJobConfigFields,
  UpdateJobConfigFields,
  UpdateJobConfigTestSetFields,
  ResumeCourseScreeningConfig,
  CreateCourseScreeningConfigFields,
  GreenhouseJob,
  EmailDripCampaign,
  PodGroup,
  EmailDripCampaignCondition,
  EmailDrip,
  ResumeScreeningJob,
  EmailDripCampaignType,
  NewCampaignRequest,
  EmailRequest,
  DripRequest,
  ResumeScreenTestCandidate,
  ResumeScreeningEvent,
  EmailDripEvent,
  SmsCommunication,
  ScreenFilter,
  EmailTemplateData,
  GetCommsHistoryResponse,
  EmailCommsTemplateStatus,
  SendgridTemplateData,
  CreateEmailTemplateRequest,
  EmailValidationResponse,
  EmailCommsTemplateVersionStatusResponse,
} from '@scale/shared-types';
import _ from 'lodash';
import { useQuery } from 'react-query';
import { SignupFlowInfo } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { ResumeParsingStatus } from '@scale/shared/types/demographics';

interface UpdateCourseScreeningConfigFields extends CreateCourseScreeningConfigFields {
  configId: string;
}

export function createJobConfig(fields: CreateJobConfigFields): Promise<ResumeScreeningConfig> {
  return apiFetch(null, 'corp-api/resume-screening/create', {
    method: 'POST',
    body: fields,
  });
}

export function updateJobConfig(fields: UpdateJobConfigFields): Promise<ResumeScreeningConfig> {
  return apiFetch(null, 'corp-api/resume-screening/update', {
    method: 'POST',
    body: fields,
  });
}

export function updateJobConfigTestSet(
  fields: UpdateJobConfigTestSetFields,
): Promise<ResumeScreeningConfig> {
  return apiFetch(null, 'corp-api/resume-screening/update-test-set', {
    method: 'POST',
    body: fields,
  });
}

export function getJobConfig(jobId: string): Promise<ResumeScreeningConfig> {
  return apiFetch(null, `corp-api/resume-screening/pipeline/${jobId}`, {
    method: 'GET',
  });
}

export function listJobConfigs(screenFilter?: ScreenFilter): Promise<ResumeScreeningConfig[]> {
  const queryParams = new URLSearchParams();
  if (screenFilter !== undefined) {
    queryParams.append('screenFilter', screenFilter);
  }
  return apiFetch(null, `corp-api/resume-screening/list?${queryParams.toString()}`, {
    method: 'GET',
  });
}

export function getJobConfigEvents(jobId: string): Promise<ResumeScreeningEvent[]> {
  return apiFetch(null, `corp-api/resume-screening/${jobId}/events`, {
    method: 'GET',
  });
}

export function deleteJobConfig(jobId: string): Promise<string> {
  return apiFetch(null, 'corp-api/resume-screening/delete', {
    method: 'POST',
    body: {
      jobId,
    },
  });
}

export function getJobStages(jobId: string): Promise<Record<string, string>> {
  return apiFetch(null, `corp-api/resume-screening/stages/${jobId}`, {
    method: 'GET',
  });
}

export const useAllActiveSignupFlowsQuery = () =>
  useQuery<SignupFlowInfo[]>(
    ['activeSignupFlows'],
    () =>
      apiFetch(null, `corp-api/onboarding-control-panel/signup-flow?active=true`, {
        method: 'GET',
      }),
    {
      refetchOnWindowFocus: false,
    },
  );

export const useListJobConfigsQuery = (screenFilter?: ScreenFilter) =>
  useQuery<ResumeScreeningConfig[]>(
    ['jobConfigs', screenFilter],
    () => listJobConfigs(screenFilter),
    {
      refetchOnWindowFocus: false,
    },
  );

export function getAllGreenhouseJobs(screenFilter?: ScreenFilter): Promise<GreenhouseJob[]> {
  const queryParams = new URLSearchParams();
  if (screenFilter !== undefined) {
    queryParams.append('screenFilter', screenFilter);
  }
  return apiFetch(null, `corp-api/resume-screening/jobs?${queryParams.toString()}`, {
    method: 'GET',
  });
}

export function screenGreenhouseJobBacklog(jobConfigId: string): Promise<any> {
  return apiFetch(null, `corp-api/resume-screening/config/trigger`, {
    method: 'POST',
    body: { jobConfigId },
  });
}

// Resume Course Screening Configs
export function createCourseScreeningConfig(
  fields: CreateCourseScreeningConfigFields,
): Promise<ResumeCourseScreeningConfig> {
  return apiFetch(null, 'corp-api/resume-course-screening/create', {
    method: 'POST',
    body: fields,
  });
}

export function updateCourseScreeningConfig(
  fields: UpdateCourseScreeningConfigFields,
): Promise<ResumeCourseScreeningConfig> {
  return apiFetch(null, `corp-api/resume-course-screening/${fields.configId}`, {
    method: 'PUT',
    body: fields,
  });
}

export function listCourseScreeningConfigs(): Promise<ResumeCourseScreeningConfig[]> {
  return apiFetch(null, 'corp-api/resume-course-screening/list', {
    method: 'GET',
  });
}

export function deleteCourseScreeningConfig(configId: string): Promise<string> {
  return apiFetch(null, `corp-api/resume-course-screening/${configId}`, {
    method: 'DELETE',
  });
}

export function listEmailDripCampaign(userId?: string): Promise<EmailDripCampaign[]> {
  let url = 'corp-api/email-drip-campaign/list';

  if (userId) {
    url += `?userId=${userId}`;
  }
  return apiFetch(null, url, {
    method: 'GET',
  });
}

export async function getEmailDripCampaign(campaignId: string): Promise<EmailDripCampaign> {
  return apiFetch(null, `corp-api/email-drip-campaign/campaign/${campaignId}`, {
    method: 'GET',
  });
}

export function getEmailDripCampaignEvents(campaignId: string): Promise<EmailDripEvent[]> {
  return apiFetch(null, `corp-api/email-drip-campaign/campaign/${campaignId}/events`, {
    method: 'GET',
  });
}

export async function getCampaignForOcpNode(
  onboardingFlowId: string,
  onboardingStepName: string,
): Promise<EmailDripCampaign> {
  return apiFetch(
    null,
    `corp-api/email-drip-campaign/ocp/${onboardingFlowId}/${encodeURIComponent(
      onboardingStepName,
    )}`,
    {
      method: 'GET',
    },
  );
}

export async function getCampaignsForOCPFlow(
  onboardingFlowId: string,
): Promise<EmailDripCampaign[]> {
  return apiFetch(null, `corp-api/email-drip-campaign/ocp/${onboardingFlowId}`, {
    method: 'GET',
  });
}

export async function getEmailDripsByCampaignId(campaignId: string): Promise<EmailDrip[]> {
  return apiFetch(null, `corp-api/email-drip-campaign/drips/${campaignId}`, {
    method: 'GET',
  });
}

export async function sendTestEmail(
  email: EmailRequest,
  podGroup: PodGroup,
  csvSubstitutions?: Record<string, string>,
  variableValues?: Record<string, string>,
): Promise<any> {
  return apiFetch(null, 'corp-api/email-drip-campaign/send-test-email', {
    method: 'POST',
    body: {
      ...email,
      podGroup,
      emailSegment: email.emailSegment,
      csvSubstitutions,
      variableValues,
    },
  });
}

function createCSVCampaignForm(
  fields: NewCampaignRequest,
  emails: DripRequest[],
  conditions: EmailDripCampaignCondition[],
): FormData {
  const formData = new FormData();
  Object.keys(fields).forEach(key => {
    let value = fields[key as keyof NewCampaignRequest] ?? '';
    if (Array.isArray(value)) {
      value = value.join(',');
    }
    formData.append(key, value as any);
  });
  formData.append('emails', JSON.stringify(emails));
  formData.append('conditions', JSON.stringify(conditions));
  return formData;
}

export function createEmailCampaignWithEmails(
  campaign: NewCampaignRequest,
  emails: DripRequest[],
  conditions: EmailDripCampaignCondition[],
): Promise<EmailDripCampaign> {
  if (campaign.type === EmailDripCampaignType.CSV) {
    return apiFetch(null, 'corp-api/email-csv-campaign/campaign', {
      method: 'POST',
      body: createCSVCampaignForm(campaign, emails, conditions),
    });
  } else {
    return apiFetch(null, 'corp-api/email-drip-campaign/campaign', {
      method: 'POST',
      body: {
        campaign,
        emails,
        conditions,
      },
    });
  }
}

export function updateEmailCampaignWithEmails(
  campaign: NewCampaignRequest,
  emails: DripRequest[],
  conditions: EmailDripCampaignCondition[],
): Promise<EmailDripCampaign> {
  if (campaign.type === EmailDripCampaignType.CSV) {
    return apiFetch(null, `corp-api/email-csv-campaign/campaign/send/${campaign._id}`, {
      method: 'PUT',
      body: createCSVCampaignForm(campaign, emails, conditions),
    });
  } else {
    return apiFetch(null, `corp-api/email-drip-campaign/campaign/${campaign._id}`, {
      method: 'PUT',
      body: {
        campaign,
        emails,
        conditions,
      },
    });
  }
}

export function deleteCampaign(campaignId: string): Promise<EmailDripCampaign> {
  return apiFetch(null, `corp-api/email-drip-campaign/campaign/${campaignId}`, {
    method: 'DELETE',
  });
}

export function pauseCampaign(campaignId: string): Promise<any> {
  return apiFetch(null, `corp-api/email-drip-campaign/campaign/${campaignId}/pause`, {
    method: 'POST',
  });
}

export function triggerResumeScreeningTest(
  jobId?: string,
  testPrompts?: string[],
  testAssistantDescription?: string,
  testCandidates?: ResumeScreenTestCandidate[],
  requiresAllQuestionsPassed?: boolean,
): Promise<any> {
  const formattedTestCandidates = testCandidates?.map(candidate => _.omit(candidate, ['_id']));
  return apiFetch(null, `corp-api/resume-screening/trigger-test`, {
    method: 'POST',
    body: {
      jobId,
      testPrompts,
      testAssistantDescription,
      testCandidates: formattedTestCandidates,
      requiresAllQuestionsPassed,
    },
  });
}

export function getResumeTestingSetUrls(
  emails: string[],
  jobConfigId?: string,
): Promise<Record<string, { cdsUrl: string; signedUrl: string }>> {
  const encodedEmails = emails.map(email => encodeURIComponent(email));
  const emailQueryParam = encodedEmails.join(',');

  let url = `corp-api/resume-screening/resume-testing-set-urls?emails=${emailQueryParam}`;
  if (jobConfigId) {
    url += `&jobConfigId=${jobConfigId}`;
  }

  return apiFetch(null, url, {
    method: 'GET',
  });
}

export function getResumeScreeningJob(jobId: string): Promise<ResumeScreeningJob> {
  return apiFetch(null, `corp-api/resume-screening/job/${jobId}`, {
    method: 'GET',
  });
}

export function getResumeScreeningJobEvents(jobId: string): Promise<ResumeScreeningEvent[]> {
  return apiFetch(null, `corp-api/resume-screening/job/${jobId}/events`, {
    method: 'GET',
  });
}

export function createResumeScreeningJob(
  fields: Partial<ResumeScreeningJob>,
): Promise<ResumeScreeningJob> {
  return apiFetch(null, 'corp-api/resume-screening/job', {
    method: 'POST',
    body: fields,
  });
}

export function updateResumeScreeningJob(
  jobId: string,
  fields: Partial<ResumeScreeningJob>,
): Promise<ResumeScreeningJob> {
  return apiFetch(null, `corp-api/resume-screening/job/${jobId}`, {
    method: 'PUT',
    body: fields,
  });
}

export function triggerOneOffScreeningJob(
  emails: string[],
  resumeScreeningJobId: string,
): Promise<any> {
  return apiFetch(null, `corp-api/resume-screening/job/trigger`, {
    method: 'POST',
    body: {
      emails,
      resumeScreeningJobId,
    },
  });
}

export function listResumeScreeningJobs(): Promise<ResumeScreeningJob[]> {
  return apiFetch(null, 'corp-api/resume-screening/job/list', {
    method: 'GET',
  });
}

export function deleteResumeScreeningJob(jobId: string): Promise<string> {
  return apiFetch(null, `corp-api/resume-screening/job/${jobId}`, {
    method: 'DELETE',
  });
}

export function getCSVFileHeaderAndFirstRow(
  campaign: NewCampaignRequest,
): Promise<Record<string, string>> {
  return apiFetch(null, `corp-api/email-csv-campaign/csv-file-first-row`, {
    method: 'GET',
    query: {
      csvCDSUrl: campaign.csvCDSUrl,
    },
  });
}

export function getCampaignByNameIfExists(
  campaignName: string,
): Promise<EmailDripCampaign | undefined> {
  return apiFetch(null, `corp-api/email-drip-campaign/campaign?campaignName=${campaignName}`, {
    method: 'GET',
  });
}

export function getContributorResume(userId: string): Promise<{
  resumeUrl: string;
  resumeText: string;
  translatedResumeText?: string;
  resumeParsingStatus?: ResumeParsingStatus;
  resumeMetadataGenerationStatus?: ResumeParsingStatus;
}> {
  return apiFetch(null, `corp-api/resume-search/resume/${userId}`, {
    method: 'GET',
  });
}

export function retriggerResumeParse(userId: string): Promise<{
  message: string;
}> {
  return apiFetch(null, `corp-api/resume-search/resume/${userId}/triggerParse`, {
    method: 'POST',
  });
}

export function removeUserResume(userId: string): Promise<{
  message: string;
}> {
  return apiFetch(null, `corp-api/resume-search/resume/${userId}`, {
    method: 'DELETE',
  });
}

export function findProjectNameById(projectId: string): Promise<any> {
  return apiFetch(null, `corp-api/project_name/${projectId}`, {
    method: 'GET',
  });
}

export function createAndSendSmsCommunication(
  fields: Partial<SmsCommunication>,
  csvFile: File,
): Promise<SmsCommunication> {
  const formData = new FormData();
  Object.keys(fields).forEach(key => {
    const value = fields[key as keyof SmsCommunication] ?? '';
    formData.append(key, value.toString());
  });
  formData.append('csvFile', csvFile);

  return apiFetch(null, 'corp-api/sms-communication/', {
    method: 'POST',
    body: formData,
  });
}

export function listSmsCommunications(): Promise<SmsCommunication[]> {
  return apiFetch(null, 'corp-api/sms-communication/', {
    method: 'GET',
  });
}

export function sendTestSms(body: string, to: string): Promise<any> {
  return apiFetch(null, 'corp-api/sms-communication/test', {
    method: 'POST',
    body: { body, to },
  });
}

export const transformCSVUrl = async (rawUrl: string) => {
  const { transformedObject: transformedUrl } = await apiFetch(
    null,
    'internal/engInternal/transform_obj_s3_url',
    {
      method: 'POST',
      body: JSON.stringify({
        inputObject: rawUrl,
        expireMs: 10 * 60 * 60 * 1000, // 10hr default expiry
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return transformedUrl;
};

export const getSmsCommunicationById = async (smsCommunicationId: string) => {
  return apiFetch(null, `corp-api/sms-communication/${smsCommunicationId}`, {
    method: 'GET',
  });
};

export const getSmsQueueStatusInPastXHours = async (hours: number) => {
  return apiFetch(null, `corp-api/sms-communication/queue-status/${hours}`, {
    method: 'GET',
  });
};

export const getEmailCommsTemplates = async ({
  status,
  latest,
}: {
  status?: EmailCommsTemplateStatus;
  latest?: boolean;
}): Promise<EmailTemplateData[]> => {
  return apiFetch(null, `corp-api/email-comms-template`, {
    method: 'GET',
    query: {
      status,
      latest,
    },
  });
};

// Returns a specific version of an email template
export const getEmailCommsTemplateVersion = async (
  templateId: string,
  version: number,
): Promise<EmailTemplateData> => {
  const templateData = await apiFetch(
    null,
    `corp-api/email-comms-template/${templateId}/versions`,
    {
      method: 'GET',
      query: {
        version,
      },
    },
  );
  if (!templateData || templateData.length === 0) {
    throw new Error('Template not found');
  }
  return templateData[0];
};

// Returns all versions of an email template
export const getEmailCommsTemplateVersions = async (
  templateId: string,
): Promise<EmailTemplateData[]> => {
  return apiFetch(null, `corp-api/email-comms-template/${templateId}/versions`, {
    method: 'GET',
  });
};

export const getEmailCommsTemplateVersionStatuses = async (
  templateVersions: { templateId: string; version: number }[],
): Promise<EmailCommsTemplateVersionStatusResponse> => {
  return apiFetch(null, `corp-api/email-comms-template/status`, {
    method: 'POST',
    body: {
      templateVersions,
    },
  });
};

export const createEmailCommsTemplate = async (
  body: CreateEmailTemplateRequest,
): Promise<EmailTemplateData[]> => {
  return apiFetch(null, `corp-api/email-comms-template`, {
    method: 'POST',
    body,
  });
};

export const updateEmailCommsTemplate = async (
  templateId: string,
  body: CreateEmailTemplateRequest,
): Promise<EmailTemplateData[]> => {
  return apiFetch(null, `corp-api/email-comms-template/${templateId}`, {
    method: 'PUT',
    body,
  });
};

export const getSendgridTemplates = async (): Promise<SendgridTemplateData[]> => {
  return apiFetch(null, `corp-api/email-comms-template/sendgrid-templates`, {
    method: 'GET',
  });
};

export const validateEmailContent = async (
  content: string,
  subject: string,
): Promise<EmailValidationResponse> => {
  return apiFetch(null, 'corp-api/email-comms-template/validate', {
    method: 'POST',
    body: { content, subject },
  });
};

export const searchCommunicationsHistoryForContributor = async (
  userIdOrEmail: string,
): Promise<GetCommsHistoryResponse> => {
  return apiFetch(null, `corp-api/communications-history/${encodeURIComponent(userIdOrEmail)}`, {
    method: 'GET',
  });
};

export const getInteractionEvents = async (emailNotificationId: string) => {
  return apiFetch(
    null,
    `corp-api/communications-history/interaction-events/${emailNotificationId}`,
    {
      method: 'GET',
    },
  );
};

export const getMostRecentCommunicationsHistory = async () => {
  return apiFetch(null, `corp-api/communications-history/`, {
    method: 'GET',
  });
};

export const getLifecycleCommTemplates = async () => {
  return apiFetch(null, `corp-api/email-comms-template/lifecycle-comms-templates`, {
    method: 'GET',
  });
};
